<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('drivers')"
      :subtitle="$t('drivers_subtitle')"
      icon="mdi-account-hard-hat"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button @click="addDriver">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_driver") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="search"
      :search-placeholder="$t('search_drivers_placeholder')"
      searchable
      :loading="isLoading"
    />

    <supplier-driver-dialog
      v-model="driverDialog"
      :edited-driver="editedDriver"
      @saved="onDriverSaved"
    />

    <!-- Empty state -->
    <div v-if="!isLoading && drivers.length === 0" class="px-6 mt-2">
      <v-card outlined>
        <v-card-text class="text-center py-12">
          <v-icon size="80" color="grey lighten-1" class="mb-4">
            mdi-account-hard-hat-outline
          </v-icon>
          <h3 class="grey--text text--darken-1 mb-2">
            {{ $t("no_drivers") }}
          </h3>
          <p class="grey--text">
            {{ $t("no_drivers_description") }}
          </p>
          <primary-button class="mt-4" @click="addDriver">
            <v-icon class="mr-2">mdi-plus-circle</v-icon>
            {{ $t("add_driver") }}
          </primary-button>
        </v-card-text>
      </v-card>
    </div>

    <!-- Desktop View -->
    <template v-else-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="filteredDrivers"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          class="striped-table ordera-box-shadow"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar color="primary" size="36" class="mr-3">
                <v-icon color="white" size="20">mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                <div v-if="item.email" class="text-caption grey--text">{{ item.email }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item.phone="{ item }">
            <div v-if="item.phone" class="d-flex align-center">
              <v-icon small class="mr-1" color="grey darken-1">mdi-phone</v-icon>
              <a :href="`tel:${item.phone}`" class="text-decoration-none black--text">{{
                item.phone
              }}</a>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.vehicle_types="{ item }">
            <v-chip
              v-for="vehicle in getVehicleTypes(item.vehicle_types)"
              :key="vehicle.value"
              small
              outlined
              color="primary"
              class="mr-1 mb-1"
            >
              <v-icon small left>{{ vehicle.icon }}</v-icon>
              {{ $t(vehicle.labelKey) }}
            </v-chip>
          </template>
          <template v-slot:item.note="{ item }">
            <div v-if="item.note" class="text-truncate" style="max-width: 200px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ item.note }}</span>
                </template>
                <span>{{ item.note }}</span>
              </v-tooltip>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.active="{ item }">
            <v-switch
              :input-value="item.active"
              inset
              dense
              hide-details
              color="success"
              class="mt-0 d-inline-flex"
              @change="toggleActive(item)"
            />
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <edit-button class="mr-2" @click="editDriver(item)" />
              <remove-button @click="deleteDriver(item)" />
            </v-row>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="filteredDrivers"
          :loading="isLoading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="2"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="24" class="mr-2">
                <v-icon color="white" size="14">mdi-account</v-icon>
              </v-avatar>
              {{ item.name }} {{ item.lastname }}
            </div>
          </template>
          <template v-slot:item.phone="{ item }">
            <div v-if="item.phone">
              <a :href="`tel:${item.phone}`" class="text-decoration-none black--text">{{
                item.phone
              }}</a>
            </div>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.vehicle_types="{ item }">
            <div v-for="vehicle in getVehicleTypes(item.vehicle_types)" :key="vehicle.value">
              <v-icon small left>{{ vehicle.icon }}</v-icon>
              {{ $t(vehicle.labelKey) }}
            </div>
          </template>
          <template v-slot:item.active="{ item }">
            <v-switch
              :input-value="item.active"
              inset
              dense
              hide-details
              color="success"
              class="mt-0 d-inline-flex"
              @change="toggleActive(item)"
            />
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editDriver(item)"></edit-button>
            <remove-button @click="deleteDriver(item)"></remove-button>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn color="primary" block @click="addDriver">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_driver") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import SupplierDriverDialog from "@/components/suppliers/SupplierDriverDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import {
  generateMockDrivers,
  generateNextDriverId,
  getVehicleTypes,
} from "@/data/supplierDrivers";

export default {
  name: "SupplierDrivers",
  components: {
    PageHeader,
    PageToolbar,
    MobileDataCards,
    SupplierDriverDialog,
  },
  mixins: [hasSnackbar],
  data: () => ({
    search: "",
    isLoading: false,
    drivers: [],
    driverDialog: false,
    editedDriver: null,
  }),
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", align: "start", width: "70px" },
        { text: this.$t("title"), value: "name", align: "start", width: "260px" },
        { text: this.$t("phone_no"), value: "phone", align: "start", width: "150px" },
        {
          text: this.$t("vehicle_type"),
          value: "vehicle_types",
          align: "start",
          width: "200px",
        },
        { text: this.$t("note"), value: "note", align: "start", width: "200px" },
        {
          text: this.$t("active"),
          value: "active",
          align: "center",
          width: "110px",
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "center",
          sortable: false,
          width: "120px",
          print: false,
        },
      ];
    },
    filteredDrivers() {
      if (!this.search) return this.drivers;
      const search = this.search.toLowerCase();
      return this.drivers.filter(
        (driver) =>
          driver.name?.toLowerCase().includes(search) ||
          driver.lastname?.toLowerCase().includes(search) ||
          driver.phone?.toLowerCase().includes(search) ||
          driver.email?.toLowerCase().includes(search)
      );
    },
    mobileHeaders() {
      return [
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("phone_no"), value: "phone", cols: 6 },
        { text: this.$t("vehicle_type"), value: "vehicle_types", cols: 6 },
        { text: this.$t("active"), value: "active", cols: 3 },
        { text: this.$t("actions"), value: "actions", cols: 3 },
      ];
    },
  },
  created() {
    this.fetchDrivers();
  },
  methods: {
    getVehicleTypes,
    fetchDrivers() {
      // No API available yet for supplier drivers, so we load local mock data.
      this.isLoading = true;
      setTimeout(() => {
        this.drivers = generateMockDrivers();
        this.isLoading = false;
      }, 400);
    },
    addDriver() {
      this.editedDriver = null;
      this.driverDialog = true;
    },
    editDriver(driver) {
      this.editedDriver = { ...driver };
      this.driverDialog = true;
    },
    onDriverSaved(payload) {
      if (payload.id) {
        const index = this.drivers.findIndex((d) => d.id === payload.id);
        if (index !== -1) this.drivers.splice(index, 1, { ...this.drivers[index], ...payload });
      } else {
        this.drivers.push({ ...payload, id: generateNextDriverId() });
      }
      this.showApiNotice();
    },
    toggleActive(driver) {
      const index = this.drivers.findIndex((d) => d.id === driver.id);
      if (index !== -1) {
        this.drivers.splice(index, 1, { ...driver, active: !driver.active });
      }
      this.showApiNotice();
    },
    deleteDriver(driver) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("delete_driver_confirmation"), {
          color: "error",
        })
        .then(() => {
          const index = this.drivers.findIndex((d) => d.id === driver.id);
          if (index !== -1) this.drivers.splice(index, 1);
          this.showApiNotice();
        })
        .catch(() => {
          // User cancelled
        });
    },
    showApiNotice() {
      this.showInfoSnackbar(this.$t("supplier_driver_api_notice"), 6000);
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
